import React, {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import Toolbar from '@material-ui/core/Toolbar'
import IconButton from '@material-ui/core/IconButton'
import ArrowLeft from '@material-ui/icons/ArrowLeft'
import SettingsPanels from './settings/SettingsPanels'
import Drawer from '@material-ui/core/Drawer'
import Typography from '@material-ui/core/Typography'
import SettingsSectionHeader from '../components/settings/SettingsSectionHeader'
import SettingsSection from '../components/settings/SettingsSection'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import LOG from '../logging/Logger'

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    height: '100%'
  },
  tabs: {
    borderRight: `1px solid ${theme.palette.divider}`
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary
  },
  grow: {
    flexGrow: 1
  }
}))

export default memo(
  ({
    version,
    showPlans = false,
    toggleDrawer,
    deleteAll,
    getExportableArea,
    getExportableProject,
    getExportableRepeat,
    getExportableTag,
    getExportableTodo,
    getAreas,
    getUnfilteredProjects,
    getProjects,
    getRepeats,
    getTags,
    getUnfilteredTodos,
    getTodos,
    createFullTodo,
    createProject,
    createArea,
    createRepeat,
    createTag,
    setInfo,
    setError,
    logout,
    isOffline,
    setShowSubscriptionPlans
  }) => {
    LOG.debug('Render: Settings')

    const classes = useStyles()
    const theme = useTheme()

    const showContent = useMediaQuery(theme.breakpoints.up('sm'), {
      noSsr: true
    })

    const [panelsState, setPanelsState] = useState(showContent)
    const [value, setValue] = useState(showContent ? 0 : -1)
    const [expanded, setExpanded] = useState('panel1')

    const togglePanels = useCallback(
      open => event => {
        if (
          event.type === 'keydown' &&
          (event.key === 'Tab' || event.key === 'Shift')
        ) {
          return
        }

        setPanelsState(open)
      },
      []
    )

    const handleChange = useCallback((event, newValue) => {
      setValue(newValue)
      setPanelsState(true)
    }, [])

    const handleExpanderChange = useCallback(
      panel => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false)
      },
      []
    )

    useEffect(() => {
      if (showPlans) {
        setExpanded('panel1')
        handleChange(null, 0)
      }
    }, [showPlans, handleChange])

    const panels = useMemo(
      () => (
        <Fragment>
          <SettingsPanels
            value={value}
            deleteAll={deleteAll}
            getExportableArea={getExportableArea}
            getExportableProject={getExportableProject}
            getExportableRepeat={getExportableRepeat}
            getExportableTag={getExportableTag}
            getExportableTodo={getExportableTodo}
            getAreas={getAreas}
            getUnfilteredProjects={getUnfilteredProjects}
            getProjects={getProjects}
            getRepeats={getRepeats}
            getTags={getTags}
            getUnfilteredTodos={getUnfilteredTodos}
            getTodos={getTodos}
            createFullTodo={createFullTodo}
            createProject={createProject}
            createArea={createArea}
            createRepeat={createRepeat}
            createTag={createTag}
            setInfo={setInfo}
            setError={setError}
            toggleDrawer={toggleDrawer}
            logout={logout}
            isOffline={isOffline}
            setShowSubscriptionPlans={setShowSubscriptionPlans}
          />
        </Fragment>
      ),
      [
        createArea,
        createFullTodo,
        createProject,
        createRepeat,
        createTag,
        getExportableArea,
        getExportableProject,
        getExportableRepeat,
        getExportableTag,
        getExportableTodo,
        deleteAll,
        getAreas,
        getTags,
        getUnfilteredProjects,
        getProjects,
        getRepeats,
        getUnfilteredTodos,
        getTodos,
        setInfo,
        setError,
        toggleDrawer,
        logout,
        value,
        isOffline,
        setShowSubscriptionPlans
      ]
    )

    const toolbar = useCallback(
      onBack => (
        <Toolbar>
          <IconButton onClick={event => onBack(event)}>
            <ArrowLeft />
          </IconButton>
          <div className={classes.grow} />
          <Typography color='textSecondary' variant='subtitle2'>
            {version}
          </Typography>
        </Toolbar>
      ),
      [classes.grow, version]
    )

    return (
      <div>
        {toolbar(event => {
          setPanelsState(false)
          toggleDrawer(event)
        })}
        <div className={classes.root}>
          <div>
            <SettingsSection
              title='Account'
              expanded={expanded}
              panelName='panel1'
              handleExpanderChange={handleExpanderChange}
            >
              <SettingsSectionHeader
                changeHandler={handleChange}
                value={0}
                title='Subscription'
              />
              <SettingsSectionHeader
                changeHandler={handleChange}
                value={1}
                title='Profile'
              />
              <SettingsSectionHeader
                changeHandler={handleChange}
                value={2}
                title='Password'
              />
              <SettingsSectionHeader
                changeHandler={handleChange}
                value={3}
                title='Automation'
              />
              <SettingsSectionHeader
                changeHandler={handleChange}
                value={4}
                title='Delete Account'
              />
            </SettingsSection>
            <SettingsSection
              title='Import / Export'
              expanded={expanded}
              panelName='panel2'
              handleExpanderChange={handleExpanderChange}
            >
              <SettingsSectionHeader
                changeHandler={handleChange}
                value={5}
                title='Import'
              />
              <SettingsSectionHeader
                changeHandler={handleChange}
                value={6}
                title='Export'
              />
            </SettingsSection>
            <SettingsSection
              title='Support'
              expanded={expanded}
              panelName='panel3'
              handleExpanderChange={handleExpanderChange}
            >
              <SettingsSectionHeader
                changeHandler={handleChange}
                value={7}
                title='Help'
              />
            </SettingsSection>
          </div>
          {showContent && panels}
          {!showContent && (
            <Drawer
              open={panelsState}
              onClose={togglePanels(false)}
              ModalProps={{
                keepMounted: true
              }}
            >
              {toolbar(() => {
                setPanelsState(false)
                if (showPlans) {
                  toggleDrawer()
                }
              })}
              <div>{panels}</div>
            </Drawer>
          )}
        </div>
      </div>
    )
  }
)
